<c-container fluid>
  <h2 class="mb-4">{{ 'wpcp.admin.matrix_title' | translate }} <c-badge color="secondary" *ngIf="version()">v{{ version() }}</c-badge></h2>

  <c-alert *ngIf="message() as m" [color]="m.type" [dismissible]="false">{{ m.text }}</c-alert>

  <!-- Community Pool split -->
  <c-card class="mb-4">
    <c-card-header><strong>{{ 'wpcp.admin.pool_split' | translate }}</strong></c-card-header>
    <c-card-body>
      <c-row class="g-2 align-items-end">
        <c-col [md]="3">
          <label class="form-label small">{{ 'wpcp.admin.community_ratio' | translate }}</label>
          <input type="number" step="0.05" min="0" max="1" class="form-control" [(ngModel)]="ratio.community_ratio" />
        </c-col>
        <c-col [md]="3">
          <label class="form-label small">{{ 'wpcp.admin.legacy_ratio' | translate }}</label>
          <input type="number" step="0.05" min="0" max="1" class="form-control" [(ngModel)]="ratio.legacy_ratio" />
        </c-col>
        <c-col [md]="3">
          <label class="form-label small">{{ 'wpcp.admin.reserve' | translate }}</label>
          <input type="number" step="0.5" min="0" class="form-control" [(ngModel)]="ratio.reserve" />
        </c-col>
        <c-col [md]="3">
          <button cButton color="primary" (click)="saveRatio()">{{ 'wpcp.admin.save' | translate }}</button>
        </c-col>
      </c-row>
      <div class="small text-medium-emphasis mt-2">{{ 'wpcp.admin.ratio_hint' | translate }}</div>
    </c-card-body>
  </c-card>

  <!-- Versioning (ch.23) -->
  <c-card class="mb-4">
    <c-card-header><strong>{{ 'wpcp.admin.versions' | translate }}</strong></c-card-header>
    <c-card-body>
      <div class="small text-medium-emphasis mb-2">{{ 'wpcp.admin.version_hint' | translate }}</div>
      <c-row class="g-2 align-items-end mb-3">
        <c-col [md]="3">
          <label class="form-label small">{{ 'wpcp.admin.version_number' | translate }}</label>
          <input class="form-control" [(ngModel)]="publishForm.version_number" placeholder="3.1" />
        </c-col>
        <c-col [md]="3">
          <label class="form-label small">{{ 'wpcp.admin.valid_from' | translate }}</label>
          <input type="datetime-local" class="form-control" [(ngModel)]="publishForm.valid_from" />
        </c-col>
        <c-col [md]="4">
          <label class="form-label small">{{ 'wpcp.admin.reason' | translate }}</label>
          <input class="form-control" [(ngModel)]="publishForm.notes" />
        </c-col>
        <c-col [md]="2">
          <button cButton color="primary" (click)="publishVersion()">{{ 'wpcp.admin.publish' | translate }}</button>
        </c-col>
      </c-row>
      <table class="table table-sm">
        <thead><tr><th>{{ 'wpcp.admin.version_number' | translate }}</th><th>{{ 'wpcp.admin.valid_from' | translate }}</th><th>Valid to</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let v of versions()">
            <td>v{{ v.version_number }}</td>
            <td class="small">{{ v.valid_from | date:'short' }}</td>
            <td class="small">{{ v.valid_to ? (v.valid_to | date:'short') : '—' }}</td>
            <td>
              <c-badge *ngIf="v.current" color="success" class="me-2">{{ 'wpcp.admin.current' | translate }}</c-badge>
              <button cButton size="sm" variant="outline" color="secondary" (click)="viewVersion(v)">{{ 'wpcp.admin.view' | translate }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </c-card-body>
  </c-card>

  <div *ngIf="loading()" class="text-center py-3"><c-spinner /></div>

  <!-- Rules per category -->
  <c-card class="mb-4" *ngFor="let c of categories()">
    <c-card-header>
      <strong>{{ c.name }}</strong>
      <c-badge class="ms-2" [color]="c.mode === 'auto' ? 'info' : 'success'">{{ c.mode }}</c-badge>
      <c-badge class="ms-1" color="secondary">{{ c.aggregation }}</c-badge>
    </c-card-header>
    <c-card-body>
      <!-- What threshold/points mean differs per category — say so, rather than
           leaving the admin to guess whether "points" is a point value or dollars. -->
      <p class="small text-medium-emphasis" *ngIf="c.description">{{ c.description }}</p>
      <table class="table table-sm align-middle">
        <thead>
          <tr>
            <th>{{ 'wpcp.admin.rule' | translate }}</th>
            <th style="width:120px">{{ 'wpcp.admin.threshold' | translate }}</th>
            <th style="width:120px">{{ 'wpcp.admin.points' | translate }}</th>
            <th style="width:110px">Min</th>
            <th style="width:110px">Max</th>
            <th style="width:230px">Formula</th>
            <th style="width:80px">{{ 'wpcp.admin.active' | translate }}</th>
            <th style="width:90px"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of rulesFor(c.key)">
            <td>{{ r.name }} <span class="text-medium-emphasis small">{{ r.code }}</span></td>
            <td><input *ngIf="c.mode === 'auto'" type="number" class="form-control form-control-sm" [(ngModel)]="r.threshold" /></td>
            <td><input *ngIf="c.mode === 'auto'" type="number" step="0.5" class="form-control form-control-sm" [(ngModel)]="r.points" /></td>
            <td><input *ngIf="c.mode === 'manual'" type="number" class="form-control form-control-sm" [(ngModel)]="r.min_points" /></td>
            <td><input *ngIf="c.mode === 'manual'" type="number" class="form-control form-control-sm" [(ngModel)]="r.max_points" /></td>
            <td>
              <div class="d-flex gap-1 align-items-center" *ngIf="paramKeys(r).length">
                <ng-container *ngFor="let k of paramKeys(r)">
                  <span class="small text-medium-emphasis">{{ paramLabel(k) }}</span>
                  <input type="number" class="form-control form-control-sm" style="width:70px"
                         [ngModel]="r.params[k]" (ngModelChange)="r.params[k] = $event"
                         [ngModelOptions]="{ standalone: true }" />
                </ng-container>
              </div>
            </td>
            <td><input type="checkbox" class="form-check-input" [(ngModel)]="r.active" /></td>
            <td><button cButton size="sm" color="primary" (click)="saveRule(r)">{{ 'wpcp.admin.save' | translate }}</button></td>
          </tr>
        </tbody>
      </table>
    </c-card-body>
  </c-card>

  <!-- Frozen snapshot of a past version -->
  <c-modal [visible]="versionModalVisible()" (visibleChange)="versionModalVisible.set($event)" size="xl" backdrop="static">
    <c-modal-header>
      <h5 cModalTitle>v{{ versionDetail()?.version?.version_number }} — {{ 'wpcp.admin.matrix_title' | translate }}</h5>
      <button cButtonClose (click)="versionModalVisible.set(false)"></button>
    </c-modal-header>
    <c-modal-body>
      <div *ngIf="versionDetail() as d">
        <div class="small text-medium-emphasis mb-2">
          {{ 'wpcp.admin.valid_from' | translate }}: {{ d.version.valid_from | date:'short' }} ·
          {{ d.version.valid_to ? (d.version.valid_to | date:'short') : ('wpcp.admin.current' | translate) }}
          <span *ngIf="d.version.notes"> · {{ d.version.notes }}</span>
        </div>
        <table class="table table-sm">
          <thead><tr><th>{{ 'wpcp.admin.category' | translate }}</th><th>{{ 'wpcp.admin.rule' | translate }}</th><th class="text-end">{{ 'wpcp.admin.threshold' | translate }}</th><th class="text-end">{{ 'wpcp.admin.points' | translate }}</th><th class="text-end">Min</th><th class="text-end">Max</th></tr></thead>
          <tbody>
            <tr *ngFor="let r of d.snapshot?.rules">
              <td class="small">{{ r.category_key }}</td>
              <td>{{ r.name }} <span class="text-medium-emphasis small">{{ r.code }}</span></td>
              <td class="text-end">{{ r.threshold }}</td>
              <td class="text-end">{{ r.points }}</td>
              <td class="text-end">{{ r.min_points }}</td>
              <td class="text-end">{{ r.max_points }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </c-modal-body>
  </c-modal>
</c-container>
